import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  Mail,
  Lock,
  ArrowLeft,
  CreditCard,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import {
  OTP_PURPOSE,
  resendOTP,
  forgotPasswordRequest,
  resetPasswordRequest,
} from "@/services/authService";
import { tokenStore } from "@/utils/tokenStore";
import { homePathFor } from "@/utils/workspace";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const Login = ({ open, onOpenChange }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const { login, verifyOtp, loading, error, setError, clearError } = useAuth();
  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false); // New state for OTP specific loading
  const [tempUserId, setTempUserId] = useState(null);
  const [resetToken, setResetToken] = useState(null); // from verifying the forgot-password OTP
  const RESEND_SECONDS = 60;
  const [resendLeft, setResendLeft] = useState(RESEND_SECONDS);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [device, setDevice] = useState(null);

  const [loginLoading, setLoginLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    logDeviceInfo();
  }, []);

  const logDeviceInfo = async () => {
    const info = await Device.getInfo();
    setDevice(info);
    console.log(info);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) clearError();
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    clearError();

    let currentLocation = null;
    setLoginLoading(true);
    // 📍 MOBILE ONLY — get GPS
    // if (device.platform !== "web") {
    //   currentLocation = await getCurrentPosition();

    //   if (!currentLocation) {
    //     toast.error("Enable location services and try again");
    //     setLoginLoading(false);

    //     return;
    //   }
    // }

    if (!formData.email.trim() || !formData.password.trim()) {
      toast.error("Please enter email and password");
      setLoginLoading(false);

      return;
    }

    const credentials = {
      username: formData.email,
      password: formData.password,
      location: currentLocation, // ✅ USE RETURN VALUE
      Device: device,
    };

    const result = await login(credentials);

    // 🔴 ADMIN + INACTIVE SUBSCRIPTION
    if (result.subscription_gate?.blocked) {
      toast.info("Your subscription is inactive.");
      if(result.user.role === "admin"){
      navigate("/subscription", {
        state: { isSubscriptionCancelled: true, reason: "renewal" },
      });
      }
      setError(result.subscription_gate.message || "Subscription inactive");
      setShowOTP(false);
      setTempUserId(null);

      setLoginLoading(false);
      return;
    }

    if (result.twoFactorRequired) {
      setShowOTP(true);
      setTempUserId(result.userId);
      setFormData((prev) => ({ ...prev, password: "" }));
      toast.info(
        "Two-Factor Authentication required. Enter OTP sent to your email."
      );

      setLoginLoading(false);
      return;
    }

    if (!result.success) {
      toast.error(result.error || "Invalid credentials", { duration: 5000 });

      setLoginLoading(false);
      return;
    }

    if (result.success) {
      toast.success("Login successful!");
      setLoginLoading(false);
    }

    // 🚦 Redirect by role
    navigate(homePathFor(result.user, { native: Capacitor.isNativePlatform() }));
  };

  const handleOtpSubmit = async () => {
    if (!otp || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP.");
      return;
    }

    if (!tempUserId) {
      toast.error(
        "Your verification session is missing. Please sign in again.",
      );
      goToLogin();
      return;
    }

    setOtpLoading(true);
    clearError();

    const isForgotModal = showResetPasswordModal === true;
    const result = await verifyOtp(tempUserId, otp, isForgotModal);
    
     setOtpLoading(false);
    if (showResetPasswordModal && result.success) {
      toast.success("OTP verified! You can now reset your password.");
      setResetToken(result.resetToken);
      setShowOTP(false);
      setShowForgotPassword(false);
      setShowResetPassword(true);
      return;
    }
    if (result.success) {
      tokenStore.set(result.access_token);
      // Redirect based on role
      navigate(homePathFor(result.user, { native: Capacitor.isNativePlatform() }));
    } else {
      toast.error(result.error || "Invalid or expired OTP");
      setOtp(""); // Clear OTP field on failure
    }
  };

  const goToLogin = () => {
    setShowOTP(false);
    setShowForgotPassword(false);
    setShowResetPassword(false);
    setShowResetPasswordModal(false);

    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setTempUserId(null);
    setResetToken(null);

    clearError();
  };

  useEffect(() => {
    if (showOTP) {
      setResendLeft(RESEND_SECONDS);
    }
  }, [showOTP]);

  // Countdown logic
  useEffect(() => {
    if (!showOTP) return;
    if (resendLeft <= 0) return;

    const id = setInterval(() => {
      setResendLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(id);
  }, [showOTP, resendLeft]);

  const canResend = resendLeft === 0 && !isResending;

  // HANDLER
  const handleResendOtp = async () => {
    if (!canResend) return;

    setIsResending(true);
    try {
      const result = await resendOTP(
        formData.email,
        showResetPasswordModal
          ? OTP_PURPOSE.PASSWORD_RESET
          : OTP_PURPOSE.LOGIN_VERIFICATION,
      );
      toast.success("A new OTP has been sent!");
      setResendLeft(RESEND_SECONDS); // restart timer
    } catch (e) {
      // 429: too many codes for now. 400: the flow is gone — start over.
      toast.error(e.response?.data?.error || "Failed to resend OTP");
      if (e.response?.status === 400) goToLogin();
    } finally {
      setIsResending(false);
    }
  };

  const handleForgotPasswordSubmit = async () => {
    if (!formData.email.trim()) {
      toast.error("Please enter your email.");
      return;
    }

    try {
      setIsLoading(true);

      const res = await forgotPasswordRequest(formData.email);
      if (res.data.userId) {
        setShowResetPasswordModal(true);
        setTempUserId(res.data.userId);
        setShowForgotPassword(false);
        setShowOTP(true);
        setIsLoading(false);

        toast.success("OTP sent to your email for password reset.!");
      }
    } catch (err) {
      setIsLoading(false);

      toast.error(err.response?.data?.error || "Failed to send reset link");
    }
  };

  const showBack = showOTP || showForgotPassword || showResetPassword;

  const headerTitle = showOTP
    ? "Verify Your OTP"
    : showResetPassword
      ? "Reset Password"
      : showForgotPassword
        ? "Forgot Password"
        : "Welcome Back";

  const headerSubtitle = showOTP
    ? "Enter the 6-digit code sent to your email."
    : showResetPassword
      ? "Create a new password for your account."
      : showForgotPassword
        ? "Enter your email to receive an OTP for reset."
        : "Please sign in to continue.";

  const handleResetPasswordSubmit = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Please enter both passwords.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }

    try {
      const result = await resetPasswordRequest(tempUserId, newPassword, resetToken);

      if (result.data) {
        toast.success("Password updated successfully!");

        // 🔥 FIX: Reset all reset-password state
        setShowResetPassword(false);
        setShowResetPasswordModal(false);
        setShowForgotPassword(false);
        setShowOTP(false);

        setNewPassword("");
        setConfirmPassword("");
        setTempUserId(null);

        // Go back to login screen
        goToLogin();
        return;
      }

      toast.error(result.error || "Failed to update password");
    } catch (err) {
      // 400: the reset session is missing or expired — start over from Forgot Password.
      if (err.response?.status === 400) {
        toast.error(err.response.data?.error || "Your reset session has expired. Please request a new OTP.");
        goToLogin();
        return;
      }
      toast.error("Server error while resetting password");
    }
  };

  const LoginContent = (
    <form onSubmit={handleLoginSubmit} className="space-y-4">
      {/* Email Field */}
      <div>
        <Label htmlFor="email">Email Address</Label>
        <div className="relative mt-1">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            placeholder="Enter your email"
            className="pl-10 dark:bg-gray-700/50 dark:border-gray-600"
            required
            autoFocus
          />
        </div>
      </div>

      {/* Password Field */}
      <div>
        <Label htmlFor="password">Password</Label>

        <div className="relative mt-1">
          {/* Left Icon */}
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />

          {/* Input */}
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            placeholder="Enter your password"
            className="pl-10 pr-10 dark:bg-gray-700/50 dark:border-gray-600"
            required
          />

          {/* Toggle Button */}
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Forgot Password - FIXED POSITION */}
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={() => {
              setShowForgotPassword(true);
              clearError();
            }}
            className="text-sm text-primary hover:underline font-medium"
          >
            Forgot password?
          </button>
        </div>
      </div>

      {/* Submit Button */}
      <div>
        <Button
          type="submit"
          className="my-3 w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-200"
          disabled={loginLoading}
        >
          {loginLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing In...
            </>
          ) : (
            "Sign In"
          )}
        </Button>
      </div>

      {/* Sign Up Link */}
      <div className="text-center pt-4 border-t border-gray-200 dark:border-gray-700">
        {Capacitor.getPlatform() ===
          "web" && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Don't have an account?{" "}
              <a
                onClick={() => navigate("/pricing")}
                className="text-primary hover:text-primary/80 font-semibold cursor-pointer"
              >
                Sign Up
              </a>
            </p>
          )}
      </div>
      <div className="flex justify-center">
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          <a
            onClick={() =>
              navigate("/policy", {
                state: "term",
              })
            }
            className="text-primary font-medium hover:underline cursor-pointer"
          >
            Terms of Service
          </a>
          {"  |  "}

          <a
            onClick={() =>
              navigate("/policy", {
                state: "policy",
              })
            }
            className="text-primary font-medium hover:underline cursor-pointer"
          >
            Privacy Policy
          </a>
        </p>
      </div>
    </form>
  );

const OtpContent = (
  <form
    className="space-y-6"
    onSubmit={(e) => {
      e.preventDefault();
      handleOtpSubmit();
    }}
  >
    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
      <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
        A <b>One-Time Password (OTP)</b> has been sent to your email address -{" "}
        <i>{formData.email}</i>
      </p>
      <p className="text-xs text-yellow-800 dark:text-yellow-300 mt-1">
        Please check your inbox and spam folder. The code is valid only for{" "}
        <b>5 minutes</b>.
      </p>
    </div>

    <div>
      <Label htmlFor="otp" className="text-gray-700 dark:text-gray-300">
        Enter OTP
      </Label>
      <Input
        id="otp"
          type="text" // Changed to text to better handle 0-padding visually, but you'll need to validate length/type on submit
        inputMode="numeric"
          pattern="[0-9]*" // Suggests numeric keyboard on mobile
        value={otp}
        onChange={(e) => {
            // Only allow digits and max length 6
          const value = e.target.value.replace(/\D/g, "").substring(0, 6);
          setOtp(value);
        }}
        placeholder="••••••"
        maxLength={6}
        className="tracking-[0.5em] text-center text-2xl font-bold h-14 mt-1 dark:bg-gray-700/50 dark:border-gray-600"
        required
        autoFocus
      />
    </div>

    <Button
      type="submit"
      className="w-full"
      disabled={otpLoading || !otp || otp.length !== 6}
    >
      {otpLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Verifying...
        </>
      ) : (
        "Verify OTP"
      )}
    </Button>
    <div className="mt-4 flex justify-end items-center gap-3">
      {resendLeft > 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Resend in <span className="font-semibold">{resendLeft}s</span>
        </p>
      ) : (
        <p className="text-xs text-green-600 dark:text-green-400">
          You can resend now
        </p>
      )}

      <button
        type="button"
        onClick={handleResendOtp}
        disabled={!canResend}
        className={[
          "text-sm font-semibold px-3 py-1 rounded-md transition border",
          canResend
            ? "text-primary border-primary/30 hover:bg-primary/10"
            : "text-gray-400 border-gray-300 cursor-not-allowed",
        ].join(" ")}
      >
        {isResending ? "Sending..." : "Resend OTP"}
      </button>
    </div>
  </form>
);

  const ForgotPasswordContent = (
    <div className="space-y-6">
      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
          Enter your email address to receive a OTP.
        </p>
        <p className="text-xs text-blue-800 dark:text-blue-300 mt-1">
          Make sure you use the email registered with Zenith.
        </p>
      </div>

      <div>
        <Label
          htmlFor="resetEmail"
          className="text-gray-700 dark:text-gray-300"
        >
          Email Address
        </Label>
        <div className="relative mt-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            id="resetEmail"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            placeholder="Enter your email"
            className="pl-10 dark:bg-gray-700/50 dark:border-gray-600"
            required
          />
        </div>
      </div>

      <Button
        className="w-full bg-primary hover:bg-primary/90 text-white shadow-md"
        loading={isLoading}
        disabled={isLoading}
        onClick={handleForgotPasswordSubmit}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
          </>
        ) : (
          "Send OTP"
        )}
      </Button>
    </div>
  );

  const ResetPasswordContent = (
    <div className="space-y-6">
      <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
        <p className="text-sm font-medium text-emerald-900 dark:text-emerald-100">
          Create a new password for your account
        </p>
        <p className="text-xs text-emerald-800 dark:text-emerald-300 mt-1">
          Make sure your password is strong and secure.
        </p>
      </div>

      {/* New Password Field */}
      <div>
        <Label htmlFor="newPassword">New Password</Label>
        <div className="relative mt-1">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />

          <Input
            id="newPassword"
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            className="pl-10 pr-10 dark:bg-gray-700/50 dark:border-gray-600"
            required
          />

          <button
            type="button"
            onClick={() => setShowNewPassword(!showNewPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            {showNewPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Confirm Password Field */}
      <div>
        <Label htmlFor="confirmPassword">Confirm Password</Label>
        <div className="relative mt-1">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />

          <Input
            id="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            className="pl-10 pr-10 dark:bg-gray-700/50 dark:border-gray-600"
            required
          />

          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            {showConfirmPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Save Button */}
      <Button
        className="w-full bg-primary hover:bg-primary/90 text-white shadow-lg"
        disabled={loading}
        onClick={handleResetPasswordSubmit}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Updating…
          </>
        ) : (
          "Update Password"
        )}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 border-0  shadow-none w-[95vw] sm:w-[90vw] md:w-[520px] lg:w-[520px] max-w-none overflow-hidden">
        <Card className="relative w-full h-full overflow-hidden shadow-2xl border-2 border-gray-200 dark:border-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm dark:shadow-[0_20px_60px_rgba(20,217,190,0.2)]">
          {/* Top Accent Line */}
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-primary via-primary/70 to-emerald-400/70" />

          {/* Header Section */}
          <CardHeader className="relative px-6 py-6 border-b border-transparent m-0">
            <div className="flex items-center justify-center relative">
              {showBack && (
                <Button
                  onClick={goToLogin}
                  className="absolute left-0 p-2 rounded-full bg-white/70 dark:bg-gray-700/70 backdrop-blur-sm shadow hover:bg-white/90 dark:hover:bg-gray-700/90 transition-all"
                >
                  <ArrowLeft className="h-4 w-4 text-gray-700 dark:text-gray-200" />
                </Button>
              )}

              <CardTitle className="text-[28px] font-bold text-gray-900 dark:text-white">
                {headerTitle}
              </CardTitle>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-2">
              {headerSubtitle}
            </p>
          </CardHeader>

          {/* Body */}
          <CardContent className="relative px-6 pb-8 m-0">
            {error && (
              <div className="group relative mb-6 overflow-hidden rounded-xl border border-red-200/50 bg-gradient-to-r from-red-50/50 to-transparent p-4 dark:from-red-950/20 dark:border-red-800/30 backdrop-blur-sm transition-all duration-300">
                {/* Decorative left accent line */}
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]" />

                <div className="flex gap-4">
                  <div
                    className={`flex ${error?.toLowerCase()?.includes("subscription") &&
                      "h-10 w-10 bg-red-100 dark:bg-red-900/40"
                      } shrink-0 items-center justify-center rounded-full text-red-600 dark:text-red-400`}
                  >
                    <AlertCircle className="h-5 w-5" />
                  </div>

                  <div className="flex-1 space-y-1">
                    {error?.toLowerCase()?.includes("subscription") && (
                      <h4 className="text-sm font-bold tracking-tight text-red-900 dark:text-red-100 uppercase">
                        Attention Required
                      </h4>
                    )}

                    <p className="text-sm leading-relaxed text-red-800/80 dark:text-red-200/70">
                      {error}
                    </p>

                    {error?.toLowerCase()?.includes("subscription") && (
                      <div className="pt-3">
                        <Button
                          onClick={() =>
                            {
                              console.log("Navigating to subscription with reason: renewal")
                              navigate("/subscription", {state: { reason: "renewal" }})
                            }
                          }
                          className="relative h-9 px-5 bg-zinc-900 hover:bg-zinc-800 dark:bg-red-600 dark:hover:bg-red-500 text-white text-xs font-semibold tracking-wide rounded-lg shadow-md hover:shadow-lg transition-all active:scale-95 flex items-center gap-2 group/btn"
                        >
                          <CreditCard className="h-3.5 w-3.5 transition-transform group-hover/btn:-rotate-12" />
                          Manage Subscription
                          <div className="absolute inset-0 rounded-lg bg-white/10 opacity-0 group-hover/btn:opacity-100 transition-opacity" />
                        </Button>

                        <p className="mt-2 flex items-center text-[11px] text-red-600/70 dark:text-red-400/60 italic font-medium">
                          <span className="mr-1 inline-block h-1 w-1 rounded-full bg-red-400 animate-pulse" />
                          Action required to restore account access
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Render Login or OTP Forms */}
            {showResetPassword
              ? ResetPasswordContent
              : showForgotPassword
                ? ForgotPasswordContent
                : showOTP
                  ? OtpContent
                  : LoginContent}
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default Login;
